import { Skeleton } from '@distribusion/owl-frontend-ui'
import { Field, useFormikContext } from 'formik'
import React, { ReactElement, useContext, useMemo } from 'react'

import PaxBreakdown from '@components/PaxBreakdown'
import TermsAndPrivacy from '@components/TermsAndPrivacy'
import TermsAndPrivacySkeleton from '@components/TermsAndPrivacy/Skeleton'
import TripBreakdown from '@components/TripBreakdown'
import TripBreakdownSkeleton from '@components/TripBreakdown/Skeleton'
import CarrierLogo from '@components/TripInfo/CarrierLogo'
import ancillaryUtils from '@lib/ancillary'
import checkoutUtils, { FEE_CODES } from '@lib/checkout'
import feeUtils from '@lib/fee'
import { useTranslation } from '@lib/i18n'
import seatUtils from '@lib/seatSelection'
import vacancyUtils from '@lib/vacancy'
import Ancillaries from '@pages/Checkout/BookingDetails/Ancillaries'
import Discount from '@pages/Checkout/BookingDetails/Discount'
import FareDetails from '@pages/Checkout/BookingDetails/FareDetails'
import Fees, { FeeData } from '@pages/Checkout/BookingDetails/Fees'
import Price from '@pages/Checkout/BookingDetails/Price'
import Seats from '@pages/Checkout/BookingDetails/Seats'
import DetailsSkeleton from '@pages/Checkout/BookingDetails/Skeleton'
import TermsAndPrivacyField from '@pages/Checkout/BookingDetails/TermAndPrivacyField'
import Tickets from '@pages/Checkout/BookingDetails/Tickets'
import TripDetails from '@pages/Checkout/BookingDetails/TripDetails'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useFinalPrice } from '@pages/Checkout/hooks/usePrice'
import { PluginContext, usePluginContext } from '@plugins/index'
import PluginSlot from '@plugins/Slot'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { useParams } from '@stores/params'
import CheckboxField from '@ui/Checkbox/Field'

import '@pages/Checkout/BookingDetails/index.scss'

interface BookingDetailsProps {
  express?: boolean
  isCarriersLoading: boolean
  carriers: MarketingCarrier[]
}

const BookingDetails = ({ express, isCarriersLoading, carriers }: BookingDetailsProps): ReactElement => {
  const [{ reservation, marketingMaterials, termsAndPrivacy, bookingDetails }] = useSettings()
  const [{ currency, hideLocations, connections, timetable }] = useParams()
  const [{ outbound, inbound }] = useCheckout()
  const { t } = useTranslation()
  const { values } = useFormikContext<CheckoutFormData>()

  const {
    vacancy,
    ancillaries,
    reservationData,
    reservationInfo,
    fareClass,
    isReservationLoading,
    isPaymentDetailsLoading,
    isVacancyLoading,
    seats,
    passengers,
    installment,
  } = values
  const { hooks } = usePluginContext()
  const {
    hooks: { useVisiblePrice = useFinalPrice },
  } = useContext(PluginContext)
  const isLoading = !outbound

  const vacancyFees = vacancy?.vacant ? checkoutUtils.filterVisibleFees(vacancy.fees) : []
  const mergedFees = [
    ...(reservationData?.fees ? reservationData.fees : []),
    ...(reservationInfo?.fees ? reservationInfo.fees : []),
  ]
  const allVisibleFees = checkoutUtils.filterVisibleFees(mergedFees)
  const paymentMethodSurcharge = allVisibleFees.filter(fee => fee.code === FEE_CODES.PAYMENT_METHOD_SURCHARGE)
  const reservationFees = allVisibleFees.filter(fee => fee.code !== FEE_CODES.PAYMENT_METHOD_SURCHARGE)
  const extraFees = hooks?.useExtraFees?.() ?? []
  const fees = reservation.enabled ? reservationFees : vacancyFees
  const priceInitialized = vacancy != null || reservationData != null
  const bookingFee = useMemo(() => {
    const outboundBookingFee = outbound?.marketingCarrier.bookingFee.fractional ?? 0
    const inboundBookingFee = inbound?.marketingCarrier.bookingFee.fractional ?? 0
    const sum = outboundBookingFee + inboundBookingFee

    if (sum > 0) return { fractional: sum, currency }
    return null
  }, [outbound, inbound, currency])

  if (bookingFee) {
    fees.push(feeUtils.buildBookingFee(bookingFee))
  }

  if (paymentMethodSurcharge?.[0]?.amount?.fractional > 0) {
    fees.push(paymentMethodSurcharge[0])
  }

  const isPriceLoading = (reservation.enabled ? isReservationLoading : isVacancyLoading) || isPaymentDetailsLoading
  const isPriceInitializing = isPriceLoading && !priceInitialized

  const seatPrice = useMemo(() => {
    const outbound = seatUtils.sumPrice(seatUtils.flatten(seats?.outbound))
    const inbound = seatUtils.sumPrice(seatUtils.flatten(seats?.inbound))

    return { fractional: outbound + inbound, currency }
  }, [currency, seats])

  const ancillariesInfo = ancillaryUtils.getPriceInfo(ancillaries, { includeReservation: true, showIncluded: true })
  const isVisibleLine = !!fees.length || !!ancillariesInfo.length || !!seatPrice.fractional || isPriceInitializing
  const showSeatPrice = !!seatPrice.fractional
  const showAncillariesInfo = !!ancillariesInfo.length
  const discount = vacancy?.vacant ? vacancyUtils.getDiscount(vacancy, reservationData) : 0
  const showTerms = marketingMaterials.enabled || termsAndPrivacy.enabled
  const visibleFees = (fees as FeeData[]).concat(extraFees)
  const selectedPassengerCodes = passengers.map(passenger => passenger.type)
  const hasCarriers = carriers.length > 0

  const totalTicketPrice = vacancy?.originalPrice ?? reservationData?.price

  return (
    <div data-tag="booking-details" className="booking-details mb-lg-2">
      <h3>{t('checkout.bookingDetails.title')}</h3>
      <Skeleton.List Skeleton={TripBreakdownSkeleton} amount={1} loading={isLoading && isCarriersLoading}>
        <>
          {hasCarriers && (
            <div className="booking-details__carriers row">
              {carriers.map(carrier => (
                <CarrierLogo key={carrier.id} name={carrier.tradeName} code={carrier.code} lineDataEnabled />
              ))}
            </div>
          )}
          <TripBreakdown
            express={express}
            carriers={carriers}
            isCarriersLoading={isCarriersLoading}
            outbound={outbound}
            inbound={inbound}
            fareClass={fareClass}
            showFareFeatures={!bookingDetails.showFareClass}
            weeklyConnections={connections}
            timetable={timetable}
          />
        </>
      </Skeleton.List>
      {bookingDetails.showFareClass && (
        <FareDetails fareClass={fareClass} carriers={carriers}>
          <hr aria-hidden={true} />
        </FareDetails>
      )}
      {!hideLocations && <hr aria-hidden={true} />}
      {!hideLocations && (
        <Skeleton.List Skeleton={DetailsSkeleton} amount={1} loading={isLoading}>
          <TripDetails fareClass={fareClass} carriers={carriers} weeklyConnections={connections} />
        </Skeleton.List>
      )}
      <PluginSlot path="checkout.bookingDetails.additionalTripDetails" />
      <hr aria-hidden={true} />
      <PaxBreakdown className="mt-4 mb-2" passengerCodes={selectedPassengerCodes} />
      <hr aria-hidden={true} />

      <Skeleton.List Skeleton={DetailsSkeleton} amount={1} loading={isPriceLoading}>
        {totalTicketPrice && <Tickets ticketCount={passengers.length} total={totalTicketPrice} />}
      </Skeleton.List>

      {showSeatPrice && <Seats price={seatPrice} />}
      {showAncillariesInfo && <Ancillaries priceInfo={ancillariesInfo} />}
      <Skeleton.List Skeleton={DetailsSkeleton} amount={1} loading={isPriceInitializing}>
        <div className="mb-4 column gap-2">
          {visibleFees.length > 0 && <Fees fees={visibleFees} />}
          {discount > 0 && <Discount amount={discount} />}
          <PluginSlot path="checkout.bookingDetails.priceBreakdown" />
        </div>
      </Skeleton.List>
      <Price price={useVisiblePrice()} isLoading={isPriceLoading} installment={installment} />

      {isVisibleLine && <hr aria-hidden={true} />}
      {showTerms && (
        <div data-tag="checkout-booking-details-terms" className="column gap-3 booking-details__terms">
          {termsAndPrivacy.enabled && (
            <Skeleton.List Skeleton={TermsAndPrivacySkeleton} amount={1} loading={isLoading || isCarriersLoading}>
              <PluginSlot
                path="checkout.bookingDetails.terms"
                FallbackComponent={TermsAndPrivacyField}
                props={{ label: <TermsAndPrivacy carriers={carriers} /> }}
              />
            </Skeleton.List>
          )}
          {marketingMaterials.enabled && (
            <Field component={CheckboxField} name="marketing" label={t('checkout.bookingDetails.marketingMaterials')} />
          )}
        </div>
      )}
    </div>
  )
}

export default BookingDetails
